import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { overrideOrganizationHeader } from '@cast/api-client';

const ENTERPRISE_PLATFORM_USAGE_DETAIL_QUERY_KEY =
  'enterprise-platform-usage-detail';

const getEnterprisePlatformUsageDetailQueryKey = (params: {
  enterpriseId: string | undefined;
  feature?: string;
  periodFrom?: string;
  periodTo?: string;
}) =>
  [
    ENTERPRISE_PLATFORM_USAGE_DETAIL_QUERY_KEY,
    params.enterpriseId,
    params.feature,
    params.periodFrom,
    params.periodTo,
  ] as const;

type GetEnterprisePlatformUsageDetailQueryOptionsParams = {
  enterpriseId: string | undefined;
  feature?: string;
  periodFrom?: string;
  periodTo?: string;
};

const getEnterprisePlatformUsageDetailQueryOptions = ({
  enterpriseId,
  feature,
  periodFrom,
  periodTo,
  enabled = true,
}: GetEnterprisePlatformUsageDetailQueryOptionsParams & {
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getEnterprisePlatformUsageDetailQueryKey({
      enterpriseId,
      feature,
      periodFrom,
      periodTo,
    }),
    queryFn: async () => {
      const { data } = await apiClient.billing.getEnterprisePlatformUsageDetail(
        {
          periodFrom: periodFrom!,
          periodTo: periodTo!,
          feature: feature!,
        },
        {
          headers: overrideOrganizationHeader(enterpriseId),
        }
      );
      return data;
    },
    enabled: makeIsEnabled(
      !!enterpriseId,
      !!feature,
      !!periodFrom,
      !!periodTo,
      enabled
    ),
  });

type UseEnterprisePlatformUsageDetailQueryArgs = {
  enterpriseId: string | undefined;
  feature?: string;
  periodFrom?: string;
  periodTo?: string;
  enabled?: boolean;
};

export const useEnterprisePlatformUsageDetailQuery = ({
  enterpriseId,
  feature,
  periodFrom,
  periodTo,
  enabled = true,
}: UseEnterprisePlatformUsageDetailQueryArgs) => {
  const { isLoading, isError, data, error, refetch } = useQuery(
    getEnterprisePlatformUsageDetailQueryOptions({
      enterpriseId,
      feature,
      periodFrom,
      periodTo,
      enabled,
    })
  );

  return { isLoading, isError, data, error, refetch };
};
